/**
 * AdminMadrassaDetail — Global Admin supervision view of one madrassa.
 *
 * Route: /admin/madrassas/:id (admin-only)
 *
 * Sections:
 *   • Header: name, status, slug, city/country, dates, créateur
 *   • Quick actions: edit, change status (active/suspended/archived)
 *   • Tabs: Membres | Classes | Demandes professeur en attente
 *
 * This is a SUPERVISION view — full madrassa management (CSV import, class
 * member edition) lives in the existing `/madrassa` page used by the
 * madrassa_admin role. Here the global admin can act on the highest-level
 * decisions (status, teacher requests, archive).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Building2, Pencil, PauseCircle, PlayCircle, Archive,
  CheckCircle2, XCircle, Users, GraduationCap, BookOpen, ShieldAlert,
} from 'lucide-react';
import { GlassCard, SectionHeader, PageLoading } from '../components/shared';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { madrassaApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUS_META = {
  pending:   { fr: 'En attente', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  active:    { fr: 'Active',     cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  suspended: { fr: 'Suspendue',  cls: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
  rejected:  { fr: 'Rejetée',    cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
  archived:  { fr: 'Archivée',   cls: 'bg-surface-elevated text-text-muted border-border' },
};

const ROLE_LABEL = {
  admin:   'Admin madrassa',
  teacher: 'Professeur',
  student: 'Élève',
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <Badge className={`${meta.cls} border text-[11px]`} data-status={status}>
      {meta.fr}
    </Badge>
  );
};

const StatChip = ({ icon: Icon, label, value, testid }) => (
  <div
    className="rounded-md border border-border bg-surface-elevated/30 px-3 py-2 flex items-center gap-2"
    data-testid={testid}
  >
    <Icon className="w-4 h-4 text-primary" />
    <div>
      <p className="text-lg font-medium text-text-primary leading-tight">{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-text-muted">{label}</p>
    </div>
  </div>
);

export const AdminMadrassaDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [mad, setMad] = useState(null);
  const [members, setMembers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAdmin = isAuthenticated && user?.role === 'admin';

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [mRes, mbRes, clRes] = await Promise.all([
        madrassaApi.get(id),
        madrassaApi.listMembers(id).catch(() => ({ data: { items: [] } })),
        madrassaApi.listClasses(id).catch(() => ({ data: { items: [] } })),
      ]);
      setMad(mRes.data || null);
      setMembers(mbRes.data?.items || []);
      setClasses(clRes.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Madrassa introuvable.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) { navigate('/', { replace: true }); return; }
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAdmin, id]);

  const partition = useMemo(() => {
    const out = { admins: [], teachers: [], students: [], pendingTeachers: [], pendingOther: [] };
    members.forEach((m) => {
      if (m.status === 'pending') {
        if (m.role === 'teacher') out.pendingTeachers.push(m);
        else out.pendingOther.push(m);
        return;
      }
      if (m.status !== 'approved') return;
      if (m.role === 'admin')   out.admins.push(m);
      if (m.role === 'teacher') out.teachers.push(m);
      if (m.role === 'student') out.students.push(m);
    });
    return out;
  }, [members]);

  const onChangeStatus = async (newStatus) => {
    try {
      await madrassaApi.adminUpdate(id, { status: newStatus });
      refresh();
    } catch { /* ignore */ }
  };

  const onDecideTeacher = async (membershipId, action) => {
    try {
      await madrassaApi.decideMembership(id, membershipId, { action });
      refresh();
    } catch { /* ignore */ }
  };

  if (authLoading || loading) return <PageLoading />;
  if (!isAdmin) return null;

  if (error || !mad) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
           data-testid="admin-madrassa-detail-error">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/madrassas')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour à la liste
        </Button>
        <p className="text-sm text-rose-300 mt-4">{error || 'Madrassa introuvable.'}</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
         data-testid="admin-madrassa-detail-page">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/admin/madrassas')}
        className="mb-3"
        data-testid="admin-madrassa-detail-back"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour à la liste
      </Button>

      {/* Header */}
      <GlassCard data-testid="admin-madrassa-detail-header">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <Building2 className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-serif text-text-primary truncate"
                  data-testid="admin-madrassa-detail-name">
                {mad.name}
              </h2>
              <StatusBadge status={mad.status} />
            </div>
            {mad.description && (
              <p className="text-sm text-text-secondary mt-1">{mad.description}</p>
            )}
            <div className="flex items-center gap-3 mt-2 flex-wrap text-[11px] text-text-muted">
              <span>Slug : <code>{mad.slug}</code></span>
              {mad.city && <span>· {mad.city}</span>}
              {mad.country && <span>· {mad.country}</span>}
              <span>· Créée : {mad.created_at?.slice(0, 10) || '—'}</span>
              {mad.updated_at && (
                <span>· MAJ : {mad.updated_at?.slice(0, 10)}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <Link to={`/admin/madrassas`}>
              <Button
                size="sm"
                variant="ghost"
                title="Modifier (depuis la liste)"
                data-testid="admin-madrassa-detail-edit"
              >
                <Pencil className="w-4 h-4" />
              </Button>
            </Link>
            {mad.status === 'active' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onChangeStatus('suspended')}
                data-testid="admin-madrassa-detail-suspend"
              >
                <PauseCircle className="w-3.5 h-3.5 mr-1 text-amber-300" /> Suspendre
              </Button>
            )}
            {mad.status === 'suspended' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onChangeStatus('active')}
                data-testid="admin-madrassa-detail-reactivate"
              >
                <PlayCircle className="w-3.5 h-3.5 mr-1 text-emerald-300" /> Réactiver
              </Button>
            )}
            {mad.status !== 'archived' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (window.confirm(`Archiver « ${mad.name} » ? Les données restent en base.`)) {
                    onChangeStatus('archived');
                  }
                }}
                data-testid="admin-madrassa-detail-archive"
              >
                <Archive className="w-3.5 h-3.5 mr-1" /> Archiver
              </Button>
            )}
          </div>
        </div>

        {mad.status === 'suspended' && (
          <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 flex items-start gap-2"
               data-testid="admin-madrassa-detail-suspended-banner">
            <ShieldAlert className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-200">
              Cette madrassa est suspendue. Statut visuel uniquement — les
              données et accès des membres existants restent intacts. Réactivez-la
              pour la rendre à nouveau visible publiquement.
            </p>
          </div>
        )}

        {/* Stat chips */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
          <StatChip icon={Users}        label="Admins"   value={partition.admins.length}
                    testid="admin-madrassa-detail-stat-admins" />
          <StatChip icon={GraduationCap} label="Profs"   value={partition.teachers.length}
                    testid="admin-madrassa-detail-stat-teachers" />
          <StatChip icon={Users}        label="Élèves"   value={partition.students.length}
                    testid="admin-madrassa-detail-stat-students" />
          <StatChip icon={BookOpen}     label="Classes"  value={classes.length}
                    testid="admin-madrassa-detail-stat-classes" />
        </div>

        <div className="mt-4">
          <Button asChild variant="outline" size="sm"
                  data-testid="admin-madrassa-detail-invite-teacher-link">
            <Link to={`/madrassa-admin/teacher-invitations?madrassaId=${id}`}>
              <GraduationCap className="w-3.5 h-3.5 mr-1" /> Inviter un professeur
            </Link>
          </Button>
        </div>
      </GlassCard>

      {/* Tabs */}
      <Tabs defaultValue="members" className="mt-6">
        <TabsList className="bg-surface border border-border">
          <TabsTrigger value="members" data-testid="admin-madrassa-detail-tab-members">
            Membres ({partition.admins.length + partition.teachers.length + partition.students.length})
          </TabsTrigger>
          <TabsTrigger value="classes" data-testid="admin-madrassa-detail-tab-classes">
            Classes ({classes.length})
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="admin-madrassa-detail-tab-pending">
            Demandes prof. ({partition.pendingTeachers.length})
          </TabsTrigger>
        </TabsList>

        {/* MEMBERS */}
        <TabsContent value="members" className="mt-4 space-y-3">
          <MemberGroup title="Administrateurs" items={partition.admins}
                       testidGroup="admins" />
          <MemberGroup title="Professeurs" items={partition.teachers}
                       testidGroup="teachers" />
          <MemberGroup title="Élèves" items={partition.students}
                       testidGroup="students" />
        </TabsContent>

        {/* CLASSES */}
        <TabsContent value="classes" className="mt-4 space-y-2">
          {classes.length === 0 && (
            <GlassCard data-testid="admin-madrassa-detail-classes-empty">
              <p className="text-sm text-text-muted text-center py-3">
                Aucune classe pour le moment.
              </p>
            </GlassCard>
          )}
          {classes.map((c) => (
            <GlassCard key={c.id} data-testid={`admin-madrassa-detail-class-${c.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">{c.name}</p>
                  {c.level && (
                    <p className="text-[11px] text-text-muted">Niveau : {c.level}</p>
                  )}
                  {c.description && (
                    <p className="text-[11px] text-text-muted mt-1 truncate">
                      {c.description}
                    </p>
                  )}
                </div>
              </div>
            </GlassCard>
          ))}
        </TabsContent>

        {/* PENDING TEACHER REQUESTS */}
        <TabsContent value="pending" className="mt-4 space-y-2">
          {partition.pendingTeachers.length === 0 && (
            <GlassCard data-testid="admin-madrassa-detail-pending-empty">
              <p className="text-sm text-text-muted text-center py-3">
                Aucune demande de professeur en attente.
              </p>
            </GlassCard>
          )}
          {partition.pendingTeachers.map((m) => (
            <GlassCard key={m.id} data-testid={`admin-madrassa-detail-pending-${m.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text-primary">
                    {m.user_name || m.user_email}
                  </p>
                  <p className="text-[11px] text-text-muted">
                    {m.user_email} · demande envoyée {m.requested_at?.slice(0, 10)}
                  </p>
                  {m.notes && (
                    <p className="text-[11px] text-text-muted mt-1 italic">« {m.notes} »</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDecideTeacher(m.id, 'reject')}
                    data-testid={`admin-madrassa-detail-pending-${m.id}-reject`}
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Refuser
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onDecideTeacher(m.id, 'approve')}
                    data-testid={`admin-madrassa-detail-pending-${m.id}-accept`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Accepter
                  </Button>
                </div>
              </div>
            </GlassCard>
          ))}
          {partition.pendingOther.length > 0 && (
            <p className="text-[11px] text-text-muted mt-2"
               data-testid="admin-madrassa-detail-pending-other-info">
              {partition.pendingOther.length} demande{partition.pendingOther.length > 1 ? 's' : ''}
              {' '}d'autre rôle (non-professeur) — gérables par l'administrateur de la madrassa
              dans <Link to="/madrassa" className="text-primary hover:underline">/madrassa</Link>.
            </p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const MemberGroup = ({ title, items, testidGroup }) => (
  <div data-testid={`admin-madrassa-detail-group-${testidGroup}`}>
    <h4 className="text-xs uppercase tracking-wider text-text-muted mb-2">
      {title} ({items.length})
    </h4>
    {items.length === 0 ? (
      <p className="text-[11px] text-text-muted">Aucun membre.</p>
    ) : (
      <div className="space-y-2">
        {items.map((m) => (
          <GlassCard
            key={m.id}
            data-testid={`admin-madrassa-detail-${testidGroup}-${m.id}`}
          >
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text-primary truncate">
                  {m.user_name || m.user_email || m.user_id}
                </p>
                <p className="text-[11px] text-text-muted truncate">
                  {m.user_email && <span>{m.user_email} · </span>}
                  {ROLE_LABEL[m.role] || m.role} ·
                  rejoint le {m.decided_at?.slice(0, 10) || m.requested_at?.slice(0, 10)}
                </p>
              </div>
            </div>
          </GlassCard>
        ))}
      </div>
    )}
  </div>
);

export default AdminMadrassaDetailPage;
